import { Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import { NAVHistoryList } from '@/modules/issuer-dashboard/components/class-tabs/NAVHistoryList.tsx';
import { NAVUpdateLogsList } from '@/modules/issuer-dashboard/components/class-tabs/NAVUpdateLogsList.tsx';
import { ButtonUpdateNav } from '@/modules/issuer-dashboard/components/nav-managment/NavHistoryTab.tsx';
import { IssuedCapitalOverTimeChart } from '@/modules/issuer-dashboard/components/securities-dashboard/IssuedCapitalOverTimeChart.tsx';
import { SelectDate } from '@/modules/issuer-dashboard/components/transactions/SelectDate.tsx';
import { ButtonGenerateAPI } from '@/modules/issuer-dashboard/pages/IssuerNavManagement.tsx';
import { SelectButtons } from 'ui';
import { RegisterClassResponse } from 'api';

enum TabOptions {
  'NAV History' = 'NAV History',
  'Update Log' = 'Update Log',
}

const ButtonsOptions = [
  { label: 'NAV History', value: TabOptions['NAV History'] },
  { label: 'Update Log', value: TabOptions['Update Log'] },
];

interface ClassNAVProps {
  classItem?: RegisterClassResponse;
  isLoading: boolean;
}

export const ClassNAV = ({ classItem }: ClassNAVProps) => {
  const [tab, setTab] = useState<TabOptions>(TabOptions['NAV History']);
  const [createdDates, setCreatedDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [activeDates, setActiveDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const handleChangeCreatedDates = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setCreatedDates(dates);
    },
    [setCreatedDates],
  );

  const handleChangeActiveDates = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setActiveDates(dates);
    },
    [setActiveDates],
  );

  return (
    <div className="flex flex-col gap-6 px-3 sm:px-6 py-4 sm:py-6">
      <IssuedCapitalOverTimeChart
        register_class_ids={classItem?.id ? [classItem?.id] : undefined}
      />
      <div className="flex 2xl:flex-row flex-col 2xl:items-center items-start 2xl:justify-between justify-start gap-4 pt-2">
        <div className="flex xl:flex-row flex-col xl:items-center items-start justify-start gap-4">
          <div className="w-[250px]">
            <SelectButtons<TabOptions>
              options={ButtonsOptions}
              selected={findDefaultValue<TabOptions>(ButtonsOptions, tab)}
              onChange={value => {
                setTab(value);
              }}
            />
          </div>
          <div className="flex lg:flex-row flex-col xl:items-center items-start gap-4">
            <div>
              <SelectDate
                id="Created"
                dates={createdDates}
                handleChangeDates={handleChangeCreatedDates}
                label="Created At"
                className="h-[36px]"
              />
            </div>
            {tab === 'NAV History' && (
              <div>
                <SelectDate
                  id="Active"
                  dates={activeDates}
                  handleChangeDates={handleChangeActiveDates}
                  label="Active From"
                  className="h-[36px]"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 h-9">
          <ButtonGenerateAPI className="min-h-8 py-0 px-2" variant="secondary" />
          <ButtonUpdateNav
            registerClassId={classItem?.id}
            variant="secondary"
            className="min-h-8 py-0 px-2"
          />
        </div>
      </div>
      <div>
        {tab === 'NAV History' && (
          <NAVHistoryList
            register_class_id={classItem?.id}
            createdDates={createdDates}
            activeDates={activeDates}
            limit={10}
            blockIdInfinite="scrollable"
          />
        )}
        {tab === 'Update Log' && (
          <NAVUpdateLogsList
            register_class_id={classItem?.id}
            createdDates={createdDates}
            limit={10}
            blockIdInfinite="scrollable"
          />
        )}
      </div>
    </div>
  );
};
